/**
 * Formato de moneda compartido (Conecta2 → COP / Colombia por defecto).
 * Convención visual: COP$ 23.500 (símbolo antes, miles con punto, sin centavos).
 */

export function isColombiaCountry(country) {
  if (country == null || country === '') return true; // producto Conecta2
  const c = String(country).trim().toLowerCase();
  return (
    c === 'colombia' ||
    c === 'co' ||
    c === 'col' ||
    c.includes('colombia')
  );
}

/**
 * Símbolo de moneda para UI (inputs, placeholders, adornments).
 * En Colombia normaliza "$" / "COP" / variantes a "COP$".
 */
export function resolveCurrencySymbol(settings = {}) {
  const country = settings.country != null ? settings.country : 'Colombia';
  const raw =
    settings.symbol != null && String(settings.symbol).trim() !== ''
      ? String(settings.symbol).trim()
      : '';
  const code = settings.code != null ? String(settings.code).trim().toUpperCase() : '';

  if (isColombiaCountry(country) || code === 'COP') {
    const n = raw.replace(/\s+/g, '').toUpperCase();
    if (!n || n === '$' || n === 'US$' || n === 'USD$' || n === 'COP' || n === 'COP$' || n === '$COP') {
      return 'COP$';
    }
    if (n.endsWith('$') || n.startsWith('$')) return 'COP$';
    return 'COP$';
  }

  return raw;
}

/**
 * Decimales de display: COP sin centavos (0); otros países respetan settings.
 */
export function resolveCurrencyDecimals(settings = {}) {
  const country = settings.country != null ? settings.country : 'Colombia';
  const code = settings.code != null ? String(settings.code).trim().toUpperCase() : '';
  if (isColombiaCountry(country) || code === 'COP') return 0;
  if (settings.decimal != null && settings.decimal !== '') {
    const d = Number(settings.decimal);
    if (Number.isFinite(d)) return Math.min(Math.max(d, 0), 2);
  }
  return 2;
}

function formatWithLocale(number, digits, locale) {
  try {
    return number.toLocaleString(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  } catch (_e) {
    return null;
  }
}

/** Fallback manual es-CO: 23500 → 23.500 / 23500.5 → 23.500,5 */
function formatEsCoManual(number, digits) {
  const fixed = number.toFixed(digits);
  const [intPart, fracPart] = fixed.split('.');
  const withDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  if (digits > 0 && fracPart != null) return `${withDots},${fracPart}`;
  return withDots;
}

/**
 * Solo el número, sin símbolo.
 * @param {number|string} value
 * @param {number} decimal
 * @param {string} country
 */
export function formatAmount(value, decimal = 0, country = 'Colombia') {
  const parsed = parseFloat(value);
  const number = Number.isFinite(parsed) ? parsed : 0;
  const digitsRaw = decimal == null || decimal === '' ? 0 : Number(decimal);
  const digits = Number.isFinite(digitsRaw) ? Math.min(Math.max(digitsRaw, 0), 2) : 0;

  if (country === 'Vietnam') {
    return (
      formatWithLocale(number, digits, 'vi-VN') ||
      formatEsCoManual(number, digits)
    );
  }

  if (isColombiaCountry(country)) {
    // COP: miles con punto (23.500). Sin centavos en producto Conecta2.
    const copDigits = digits; // caller ya suele pasar 0 vía resolveCurrencyDecimals
    return (
      formatWithLocale(number, copDigits, 'es-CO') ||
      formatEsCoManual(number, copDigits)
    );
  }

  return (
    formatWithLocale(number, digits, 'en-US') ||
    number.toFixed(digits)
  );
}

/**
 * Número + símbolo (COP$ 23.500 en Colombia).
 * @param {number|string} value
 * @param {object} settings - { symbol, decimal, country, code }
 * @param {object} [options] - { isRTL }
 */
export function formatMoney(value, settings = {}, options = {}) {
  const country = settings.country != null ? settings.country : 'Colombia';
  const decimal = resolveCurrencyDecimals(settings);
  const amount = formatAmount(value, decimal, country);
  const symbol = resolveCurrencySymbol(settings);

  if (!symbol) return amount;
  if (options.isRTL) return `${amount} ${symbol}`.trim();
  // Espacio entre símbolo y monto (COP$ 23.500)
  return `${symbol} ${amount}`;
}

/**
 * Alias COP estricto.
 */
export function formatCop(amount) {
  return formatMoney(amount, { country: 'Colombia', code: 'COP', decimal: 0 });
}
